import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SelectTemplateProps } from './types';
import { TemplatesTooltipCardWrapper } from './styles';
import Select from '@mui/material/Select';
import {
  CircularProgress,
  ClickAwayListener,
  FormControl,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import TemplateCard from './templateCard';
import { useAuth } from 'context/auth';
import { useRoleCapabilities } from 'hooks/useRoleCapabilities';
import { FlexDiv } from 'crono-fe-common/components/Layout/FlexDiv';
import { Template as _Template } from 'crono-fe-common/types/template';
import { useNavigate } from 'react-router-dom';
import CloseMIcon from 'crono-fe-common/icons/Icon-Close';
import { colors } from 'crono-fe-common/theme';
import useSearchTemplates from 'hooks/services/templates/useSearchTemplates';
import TemplateType from 'crono-fe-common/types/enums/templateType';
import usePatchUserPreferences from 'hooks/services/user/usePatchUserPreferences';
import { Constants } from 'crono-fe-common/constants/constants';
import SearchIcon from 'crono-fe-common/icons/Icon-Search';
import ExternalPropertyFilter from 'crono-fe-common/types/externalPropertyFilter';
import { externalPropertiesToTagInsert } from 'crono-fe-common/utils';
import CronoSwitch from 'crono-fe-common/components/CronoSwitch';
import IconTopArrow from 'crono-fe-common/icons/Icon-Top-Arrow';
import useGetUsers from 'hooks/services/subscription/useGetUsers';
import LanguagePicker from 'components/LanguagePicker';
import { LanguageOption } from 'crono-fe-common/types/languageOption';
import TagsFilterComponent from 'pages/lists/contactTable/strategySelector/tagsFilterComponent';
import { useCustomLocation } from 'context/customLocation';
import { TemplateSearchSort } from 'crono-fe-common/types/DTO/templateSearch';
import TemplatesSortPicker, {
  TEMPLATE_SORT_OPTIONS,
} from 'crono-fe-common/components/TemplatesSortPicker';
import ListDropdown from 'pages/lists/Lists/ListDropdown';
import { List, ListType } from 'crono-fe-common/types/list';
import { TemplateFilters } from 'crono-fe-common/types/templateFilters';
import { useCreateNewTemplateNavigation } from 'pages/templates/navigation/useCreateNewTemplateNavigation';
import { legacyUserToUserIds, userIdsToLegacyUser } from 'hooks/useUsersFilter';

const customMenuProps = {
  style: { zIndex: 10000 },
  PaperProps: {
    sx: {
      zIndex: 10001,
      borderRadius: '8px',
      paddingInline: '6px',
      border: '1px solid #dadde9',
      boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
      '& .MuiMenuItem-root': {
        fontSize: '12px',
        lineHeight: '16px',
        height: 'fit-content',
        padding: '8px',
        cursor: 'pointer',
        width: '100%',
        borderRadius: '8px',
        '&:hover': {
          background: colors.primaryLight,
          color: colors.primary,
        },
      },
      '& .Mui-selected': {
        color: colors.primary,
        backgroundColor: 'transparent !important',
      },
    },
  },
};

const TemplatesTooltipCard: FC<SelectTemplateProps> = ({
  prospectId,
  selectedTemplateId,
  setSelectedTemplate,
  selectedType,
  hideShared,
  hideAll,
  showAnalytics,
  close,
  account,
  prospect,
  strategyStepTemplateId,
  allowTemplateCreation = true,
}) => {
  const { user } = useAuth();
  const { isManager } = useRoleCapabilities();
  const { data: users } = useGetUsers();
  const navigate = useNavigate();

  // Parse saved filters from JSON string with error handling
  const parseTemplateFilters = (
    filtersString: string | null | undefined,
  ): TemplateFilters | null => {
    if (!filtersString) return null;
    try {
      return JSON.parse(filtersString) as TemplateFilters;
    } catch (error) {
      console.warn(
        'Failed to parse template filters from user preferences:',
        error,
      );
      return null;
    }
  };

  const [search, setSearch] = useState<string>('');

  const [selectedLanguage, setSelectedLanguage] =
    React.useState<LanguageOption>(Constants.templateLanguages[0]);

  const [showSelectUserFilter, setShowSelectUserFilter] =
    useState<boolean>(false);

  // tag filters --------------------
  const [filtersVisible, setFiltersVisible] = useState<boolean>(false);

  const [filterExternalProperties, setFilterExternalProperties] = useState<
    ExternalPropertyFilter[]
  >([]);
  const flatTagsFilter = useMemo(() => {
    return externalPropertiesToTagInsert(filterExternalProperties);
  }, [filterExternalProperties]);

  const [selectedUser, setSelectedUser] = useState<number | undefined>(-1);

  const [sortOption, setSortOption] = useState<TemplateSearchSort>(
    TEMPLATE_SORT_OPTIONS[0],
  );

  // Initialize filters from user preferences when user data is available
  useEffect(() => {
    if (user?.userPreferences) {
      const savedFilters = parseTemplateFilters(
        user.userPreferences.templateFilters,
      );

      if (savedFilters?.language) {
        setSelectedLanguage(savedFilters.language);
      } else if (
        user.userPreferences.templateLanguage &&
        Constants.templateLanguages.includes(
          user.userPreferences.templateLanguage,
        )
      ) {
        setSelectedLanguage(user.userPreferences.templateLanguage);
      }

      if (savedFilters?.sort) {
        setSortOption(savedFilters.sort);
      }

      // Derive the single owner from the canonical multi-owner arrays (with
      // legacy fallback) so this picker stays in sync with the templates page,
      // which stores the selection as userIds/userListIds.
      const canonicalUserIds =
        savedFilters?.userIds ??
        legacyUserToUserIds(savedFilters?.selectedUser);
      const canonicalTeamIds = savedFilters?.userListIds ?? [];
      setSelectedUser(
        userIdsToLegacyUser(canonicalUserIds, canonicalTeamIds) ?? -1,
      );

      if (savedFilters?.externalProperties) {
        setFilterExternalProperties(savedFilters.externalProperties);
      }
    }
  }, [user?.userPreferences]);

  const [selectedList, setSelectedList] = useState<List | null>(null);

  const { mutate: patchUserPreferences } = usePatchUserPreferences();

  // Helper function to save template filters to user preferences
  const saveTemplateFilters = useCallback(
    (updates: Partial<TemplateFilters>) => {
      const currentFilters =
        parseTemplateFilters(user?.userPreferences?.templateFilters) || {};
      const newFilters: TemplateFilters = {
        ...currentFilters,
        ...updates,
      };

      const serializedFilters = JSON.stringify(newFilters);

      // Prevent redundant API calls by checking if the serialized filters actually changed
      if (user?.userPreferences?.templateFilters === serializedFilters) {
        return;
      }

      patchUserPreferences({ templateFilters: serializedFilters });

      // Update user object in memory to reflect the change
      if (user) {
        if (user.userPreferences) {
          user.userPreferences.templateFilters = serializedFilters;
        } else {
          user.userPreferences = { templateFilters: serializedFilters };
        }
      }
    },
    [user, patchUserPreferences],
  );

  // --------------------------

  const {
    data: templates,
    isLoading: loadingTemplates,
    isError: fetchTemplatesError,
  } = useSearchTemplates({
    limit: Constants.defaultTemplatesLimit,
    offset: 0,
    prospectId: prospectId,
    type: selectedType,
    language: selectedLanguage === 'all' ? undefined : selectedLanguage,
    userId: selectedUser === -1 ? undefined : selectedUser,
    title: search,
    templateTags: flatTagsFilter.length ? flatTagsFilter : undefined,
    filterByTags: flatTagsFilter.length ? true : false,
    strategyStepTemplateId: strategyStepTemplateId,
    sort: sortOption,
    listId: selectedList?.id || null,
    //If we are looking for the templates of another user, we have to show only the shared ones if we are not a manager
    ...(selectedUser !== user?.id && !isManager && { shared: true }),
    withContent: true,
    withTags: true,
  });

  const handleChangeLanguage = (option: LanguageOption) => {
    setSelectedLanguage(option);
    saveTemplateFilters({ language: option });
  };

  const handleChangeSortOption = (sort: TemplateSearchSort) => {
    setSortOption(sort);
    saveTemplateFilters({ sort });
  };

  const handleChangeSelectedUser = (userId: number | undefined) => {
    setSelectedUser(userId);
    saveTemplateFilters({
      selectedUser: userId,
      userIds: legacyUserToUserIds(userId),
    });
  };

  const handleChangeExternalProperties: React.Dispatch<
    React.SetStateAction<ExternalPropertyFilter[]>
  > = (value) => {
    let newProperties: ExternalPropertyFilter[];
    if (typeof value === 'function') {
      newProperties = value(filterExternalProperties);
    } else {
      newProperties = value;
    }
    setFilterExternalProperties(newProperties);
    saveTemplateFilters({ externalProperties: newProperties });
  };

  if (fetchTemplatesError) throw fetchTemplatesError;

  function renderTemplateAnalyticsHeader() {
    return (
      <FlexDiv className="template-analytics-header-container">
        <div className="flex items-center">
          <Typography fontSize={'12px'} fontWeight={500}>
            Sort by:
          </Typography>

          <TemplatesSortPicker
            sortOption={sortOption}
            setSortOption={handleChangeSortOption}
            maxWidth={150}
            noBorder={true}
            noPadding={true}
            templateType={selectedType}
          />
        </div>

        <div
          style={{
            display: 'flex',
            fontSize: 12,
            justifyContent: 'flex-end',
            textAlign: 'center',
          }}
        >
          <span style={{ width: 54 }}>Usage</span>
          {selectedType === TemplateType.EMAIL && (
            <span style={{ width: 54 }}>Open</span>
          )}
          <span style={{ width: 54 }}>
            {selectedType === TemplateType.INVITATION ? 'Accept' : 'Reply'}
          </span>
        </div>
      </FlexDiv>
    );
  }
  const { setCustomStateLocation } = useCustomLocation();
  const { setShowCreateModal } = useCreateNewTemplateNavigation();

  const openTemplatesPage = useCallback(() => {
    setShowCreateModal(true);
    setCustomStateLocation({
      initialTemplateModalState: {
        templateType: selectedType,
      },
    });
  }, [navigate, selectedType, setCustomStateLocation]);

  const handleSelectTemplate = (template: _Template) => {
    setSelectedTemplate(template);
    close();
  };

  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  const myTemplatesOn: boolean = selectedUser === user?.id;

  return (
    <>
      <ClickAwayListener onClickAway={close} id="clickaway-template-tooltip">
        <div
          id="wrapper-template-tooltip"
          style={{
            height: 600,
          }}
        >
          <TemplatesTooltipCardWrapper
            style={{
              height: '100%',
            }}
          >
            <div
              className="template-selector"
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <div className="templates-card-header">
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    width: '100%',
                  }}
                >
                  <div className="search-templates">
                    <SearchIcon color={colors.grey11} className="search-icon" />
                    <TextField
                      className="text-field"
                      variant="standard"
                      placeholder="Search..."
                      autoComplete="off"
                      inputRef={searchInputRef}
                      InputProps={{
                        disableUnderline: true,

                        endAdornment:
                          (search || '') === '' ? null : (
                            <CloseMIcon
                              className="remove-text-icon"
                              color={colors.grey11}
                              onClick={() => setSearch('')}
                            />
                          ),
                      }}
                      sx={{
                        width: '100%',
                        input: {
                          fontWeight: '400',
                          padding: '4px 0px',
                          '&::placeholder': {
                            opacity: 1,
                            color: colors.grey11,
                          },
                        },
                      }}
                      value={search}
                      onChange={(ev) => setSearch(ev.target.value)}
                    />

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        fontWeight={500}
                        lineHeight={'16px'}
                        fontSize={12}
                        color={colors.grey11}
                        whiteSpace={'nowrap'}
                      >
                        My templates
                      </Typography>
                      <CronoSwitch
                        checked={myTemplatesOn}
                        onChange={() => {
                          const newUser =
                            selectedUser === user?.id ? -1 : user?.id;
                          handleChangeSelectedUser(newUser);
                        }}
                      />
                      {filtersVisible && (
                        <IconButton
                          onClick={(ev) => {
                            ev.stopPropagation();
                            ev.preventDefault();
                            setFiltersVisible(false);
                          }}
                          style={{ height: 'fit-content' }}
                        >
                          <IconTopArrow />
                        </IconButton>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2 w-full">
                    <LanguagePicker
                      language={selectedLanguage}
                      onLanguageChange={handleChangeLanguage}
                      hideOptionAll={hideAll}
                      triggerClassName={myTemplatesOn ? 'w-29.5' : 'w-22'}
                      contentClassName="z-[10001]"
                    />
                    {/* Tags filter */}
                    <TagsFilterComponent
                      filterExternalProperties={filterExternalProperties}
                      setFilterExternalProperties={
                        handleChangeExternalProperties
                      }
                      shortSelectedLabel={true}
                      customWidth={myTemplatesOn ? 118 : 88}
                    />
                    {/* List filter */}
                    <div
                      className="relative"
                      style={{
                        minWidth: myTemplatesOn ? 118 : 88,
                        maxWidth: myTemplatesOn ? 118 : 88,
                      }}
                    >
                      <ListDropdown
                        selectedList={selectedList}
                        setSelectedList={setSelectedList}
                        listType={ListType.Template}
                        dropdownWidth={myTemplatesOn ? '300px' : '240px'}
                        inset="42px 0 auto auto"
                        customEmptyText="All lists"
                        hideCrossAndIntroduceAllOption={true}
                      />
                    </div>{' '}
                    {/* User filter */}
                    {!myTemplatesOn && (
                      <ClickAwayListener
                        onClickAway={() => setShowSelectUserFilter(false)}
                      >
                        <FormControl
                          sx={{ maxWidth: 88, mt: 1, mb: 1 }}
                          size="small"
                        >
                          <Select
                            labelId="select-medium"
                            id="select-medium"
                            value={selectedUser ?? -1}
                            onClick={() =>
                              setShowSelectUserFilter((prev) => !prev)
                            }
                            onChange={(e) => {
                              handleChangeSelectedUser(
                                (e.target.value ?? -1) as number,
                              );
                            }}
                            open={showSelectUserFilter}
                            sx={{
                              fontSize: '14px',
                              boxShadow: 'none',
                              height: '40px',
                              '.MuiOutlinedInput-notchedOutline': {
                                border: `1px solid ${colors.grey3}`,
                                borderRadius: 2,
                              },
                            }}
                            MenuProps={customMenuProps}
                          >
                            <MenuItem value={-1}>All users</MenuItem>
                            {users?.data?.data.map((user) => {
                              return (
                                <MenuItem value={user.id} key={user.id}>
                                  {user.firstName} {user.lastName}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </ClickAwayListener>
                    )}
                  </div>
                </div>
              </div>

              {loadingTemplates ? (
                //The height here is given so that the first time the template loads it goes at full height, so that the tooltip can position it well
                <FlexDiv style={{ minHeight: 500 }}>
                  <CircularProgress />
                </FlexDiv>
              ) : (
                <div className="templates-container">
                  {showAnalytics &&
                    (templates?.data?.data.length || 0) > 0 &&
                    renderTemplateAnalyticsHeader()}
                  <div
                    style={{
                      flex: 1,
                      overflow: 'hidden auto',
                    }}
                  >
                    {templates?.data?.data.map((template) => {
                      return (
                        <>
                          <TemplateCard
                            key={template.id}
                            template={template}
                            showAnalytics={showAnalytics}
                            handleSelectTemplate={handleSelectTemplate}
                            selected={selectedTemplateId === template.id}
                            usedForProspect={template.usedForProspect}
                            prospect={prospect}
                            account={account}
                            showTags
                            smallCard={true}
                          />
                          <div
                            style={{ border: `1px solid ${colors.grey4}` }}
                          />
                        </>
                      );
                    })}
                  </div>

                  {showAnalytics && templates?.data?.data.length === 0 && (
                    <div className="no-templates">
                      <Typography fontSize="14px">
                        <span
                          className="no-templates-link"
                          onClick={openTemplatesPage}
                        >
                          Click
                        </span>{' '}
                        to create a new template.
                      </Typography>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TemplatesTooltipCardWrapper>
        </div>
      </ClickAwayListener>
    </>
  );
};

export default TemplatesTooltipCard;
